import { useMutation, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { useCallback } from 'react';

import { useHttpClient } from '@bonusly/http-client';
import { Permission, usePermission } from '@bonusly/who-am-i';

import { Goal, GoalApiData } from '../../../types/goal';
import { QueryKey as TeamManagementQueryKey } from '../../team-management/types/team-management-types';
import { QueryKey } from '../types/growth-query-key';

interface CreateGoalRequest {
	userId: string;
	title: string;
	summary?: string;
	startDate?: string;
	endDate?: string;
	tags: string[];
	status?: string;
	collaborators?: string[];
	initiativeId?: string;
}

interface UpdateGoalRequest extends Partial<CreateGoalRequest> {
	status?: string;
	completedAt?: Date;
	collaborators?: string[]; // Still array of user IDs for API requests
	initiativeId?: string;
}

interface AddGoalActivityRequest {
	goalId: string;
	activityType: string;
	activityId?: string;
	activityData?: {
		title?: string;
		summary?: string;
		[key: string]: any;
	};
}

// Custom hooks
export const useGoals = (userId?: string, options = {}) => {
	const http = useHttpClient();
	const canAccessGoals = usePermission(Permission.CanAccessGoals);

	return useQuery<Goal[], unknown, Goal[]>(
		[QueryKey.Goals, userId],
		async () => {
			const params = userId ? { user_id: userId } : {};
			const res = await http.get<GoalApiData[]>('/achieve/goals', {
				params,
			});
			return res.data.map((goalData) => Goal.fromApiData(goalData));
		},
		{
			refetchOnWindowFocus: true, // Enable refetch when user returns to goals page
			staleTime: 60 * 1000, // Consider data stale after 60 seconds
			...options,
			// Only enabled if user has Goals access
			enabled: canAccessGoals,
		}
	);
};

export const useGoal = (id: string, options: UseQueryOptions<Goal, unknown, Goal> = {}) => {
	const http = useHttpClient();
	const canAccessGoals = usePermission(Permission.CanAccessGoals);

	return useQuery<Goal, unknown, Goal>(
		[QueryKey.Goal, id],
		async () => {
			const res = await http.get<GoalApiData>(`/achieve/goals/${id}`);
			return Goal.fromApiData(res.data);
		},
		{
			refetchOnWindowFocus: false,
			...options,
			// Only enabled if ID exists and user has Goals access
			enabled: !!id && canAccessGoals && options.enabled !== false,
		}
	);
};

export const useCreateGoal = () => {
	const http = useHttpClient();
	const queryClient = useQueryClient();

	return useMutation(
		async (goalData: CreateGoalRequest) => {
			// Transform camelCase to snake_case for Rails backend
			const payload = Goal.toApiData(goalData);

			const res = await http.post<GoalApiData>('/achieve/goals', payload);
			return Goal.fromApiData(res.data);
		},
		{
			onSuccess: () => {
				queryClient.invalidateQueries({ queryKey: [QueryKey.Goals] });
				queryClient.invalidateQueries({ queryKey: [TeamManagementQueryKey.DirectReportsOverview] });
			},
		}
	);
};

export const useUpdateGoal = () => {
	const http = useHttpClient();
	const queryClient = useQueryClient();

	return useMutation(
		async ({ id, data }: { id: string; data: UpdateGoalRequest }) => {
			// Transform camelCase to snake_case for Rails backend
			const payload = Goal.toApiData(data);

			const res = await http.put<GoalApiData>(`/achieve/goals/${id}`, payload);
			return Goal.fromApiData(res.data);
		},
		{
			onSuccess: (_, { id }) => {
				queryClient.invalidateQueries({ queryKey: [QueryKey.Goals] });
				queryClient.invalidateQueries({ queryKey: [QueryKey.Goal, id] });
			},
		}
	);
};

export const useDeleteGoal = () => {
	const http = useHttpClient();
	const queryClient = useQueryClient();

	return useMutation(
		async (id: string) => {
			const res = await http.delete<GoalApiData>(`/achieve/goals/${id}`);
			return Goal.fromApiData(res.data);
		},
		{
			onSuccess: () => {
				queryClient.invalidateQueries({ queryKey: [QueryKey.Goals] });
			},
		}
	);
};

export const useAddGoalUpdate = () => {
	const http = useHttpClient('v2');
	const queryClient = useQueryClient();

	return useMutation(
		async ({ goalId, activityType, activityId, activityData }: AddGoalActivityRequest) => {
			const payload: any = {
				activity_type: activityType,
			};

			if (activityId) {
				payload.activity_id = activityId;
			}

			if (activityData) {
				payload.activity_data = activityData;
			}

			const res = await http.post<GoalApiData>(`/achieve/goals/${goalId}/activities`, payload);
			return Goal.fromApiData(res.data);
		},
		{
			onSuccess: (_, { goalId }) => {
				queryClient.invalidateQueries({ queryKey: [QueryKey.Goals] });
				queryClient.invalidateQueries({ queryKey: [QueryKey.Goal, goalId] });
			},
		}
	);
};

// Cache invalidation utilities
export const useInvalidateGoals = () => {
	const queryClient = useQueryClient();

	return useCallback(() => {
		queryClient.invalidateQueries({ queryKey: [QueryKey.Goals] });
	}, [queryClient]);
};

export const useInvalidateGoalById = () => {
	const queryClient = useQueryClient();

	return useCallback(
		(goalId: string) => {
			queryClient.invalidateQueries({ queryKey: [QueryKey.Goal, goalId] });
		},
		[queryClient]
	);
};

export const useRefreshGoals = () => {
	const queryClient = useQueryClient();

	return useCallback(() => {
		queryClient.refetchQueries([QueryKey.Goals]);
	}, [queryClient]);
};

// Utility functions
export const useGoalsActions = () => {
	const createGoal = useCreateGoal();
	const updateGoal = useUpdateGoal();
	const deleteGoal = useDeleteGoal();
	const addUpdate = useAddGoalUpdate();

	const completeGoal = useCallback(
		(goalId: string) => {
			return updateGoal.mutateAsync({
				id: goalId,
				data: { status: 'Completed', completedAt: new Date() },
			});
		},
		[updateGoal]
	);

	const archiveGoal = useCallback(
		(goalId: string) => {
			return deleteGoal.mutateAsync(goalId);
		},
		[deleteGoal]
	);

	return {
		createGoal: createGoal.mutateAsync,
		updateGoal: updateGoal.mutateAsync,
		deleteGoal: deleteGoal.mutateAsync,
		addUpdate: addUpdate.mutateAsync,
		completeGoal,
		archiveGoal,
		isLoading: createGoal.isLoading || updateGoal.isLoading || deleteGoal.isLoading || addUpdate.isLoading,
	};
};
